<template>
    <div ref="headerContainer"></div>
</template>

<script lang="ts">
    import VueMq from "vue-mq";
    import { Component, Vue, Watch } from "vue-property-decorator";
    import { NavigationGuardNext, Route } from "vue-router";
    import { Action, Getter } from "vuex-class";
    import i18n from "@/i18n";
    import router, { campaignsRoute, clientLeadsRoute, clientsRoute, leadsRoute } from "@/router";
    import { RefreshGridMode } from "@/models/leads/Lead";
    import { MenuLink, UserData } from "@/models/UserData";
    import ConfigService from "@/services/ConfigService";
    import HelperService from "@/services/HelperService";
    import ApiService from "@/services/api/ApiService";

    Vue.use(VueMq, {
        breakpoints: {
            sm: 576,
            md: 992,
            lg: Infinity,
        },
        defaultBreakpoint: "sm",
    });

    const namespace = "mainModule";

    @Component({})
    export default class Header extends Vue {
        @Getter("userData", { namespace }) userData!: UserData;
        @Action("getUserData", { namespace }) getUserData!: () => Promise<UserData>;
        @Action("setNeedToRefreshCampaignsGrid", { namespace: "campaignsModule" }) setNeedToRefreshCampaignsGrid!: (needToRefreshCampaignsGrid: RefreshGridMode) => void;
        @Action("setNeedToRefreshLeadsGrid", { namespace: "leadsModule" }) setNeedToRefreshLeadsGrid!: (needToRefreshLeadsGrid: RefreshGridMode) => void;
        @Action("setUserData", { namespace }) setUserData!: (userData: UserData | null) => void;

        routeChanged = false;
        private headerRenderer: any = null;
        // Prevents double render on initial SPA load (route/user updates can race), which would duplicate header init and requests
        private headerRendering = false;

        mounted(): void {
            this.loadHeaderStyles();

            this.loadHeaderScript().then(() => {
                this.$nextTick(async () => {
                    if (this.userData && !this.headerRenderer) {
                        await this.renderUniversalHeader();
                    }
                });
            });

            router.beforeEach(async (to: Route, from: Route, next: NavigationGuardNext<Vue>) => {
                this.routeChanged = true;
                await this.$nextTick();
                if (this.userData && this.headerRenderer) {
                    const updateHeader = (window as any).updateHeader;
                    if (updateHeader) {
                        await updateHeader(this.headerRenderer, this.userData);
                    }
                } else if (this.userData && !this.headerRenderer) {
                    await this.renderUniversalHeader();
                }
                next();
            });
            this.checkAndUpdateHeader();
        }

        private loadHeaderStyles(): void {
            if (document.querySelector('link[href="/Admin/css/header/header.css"]')) {
                return;
            }

            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = '/Admin/css/header/header.css';
            document.head.appendChild(link);
        }

        private loadHeaderScript(): Promise<void> {
            return new Promise((resolve, reject) => {
                if ((window as any).renderHeader) {
                    resolve();
                    return;
                }
                // Avoid duplicate injection if the script tag already exists (e.g., inserted by Razor init)
                const existing = document.querySelector('script[src="/Admin/js/header/index.js"]') as HTMLScriptElement | null;
                if (existing) {
                    // If it is already loaded, resolve immediately; otherwise wait for load
                    if ((window as any).renderHeader) {
                        resolve();
                        return;
                    }
                    const onLoad = () => resolve();
                    existing.addEventListener('load', onLoad, { once: true } as any);
                    // Fallback: if renderHeader appears before load event fires
                    const checkInterval = window.setInterval(() => {
                        if ((window as any).renderHeader) {
                            window.clearInterval(checkInterval);
                            resolve();
                        }
                    }, 50);
                    return;
                }

                const script = document.createElement('script');
                script.src = '/Admin/js/header/index.js';
                script.type = 'module';
                script.onload = () => resolve();
                script.onerror = () => reject(new Error('Failed to load header script'));
                document.head.appendChild(script);
            });
        }

        private async renderUniversalHeader(): Promise<void> {
            if (!this.userData) {
                return;
            }
            if (this.headerRenderer || this.headerRendering) {
                return;
            }

            const renderHeader = (window as any).renderHeader;
            if (!renderHeader) {
                console.error('renderHeader function not found');
                return;
            }

            const container = this.$refs.headerContainer as HTMLElement;
            if (!container) {
                return;
            }

            if (container.children.length > 0) {
                container.innerHTML = '';
            }

            this.headerRendering = true;
            try {
                this.headerRenderer = await renderHeader(container, this.userData, {
                    t: (key: string, params?: Record<string, any>) => {
                        return this.$t(key, params).toString();
                    },
                    onUserDropdownOpen: () => { /* no-op */ },
                    onUserInfoClick: (e: Event) => {
                        e.preventDefault();
                        this.showUserInfo(e);
                    },
                    onMenuItemClick: (e: Event, menuLink: MenuLink) => {
                        this.onMenuItemClick(e, menuLink);
                    },
                    onLocaleChange: (locale: string) => {
                        this.changeLocale(locale);
                    },
                    onLogout: () => {
                        this.logout();
                    },
                    agencyAdminRole: ConfigService.agencyAdminUserRole,
                    leadViewerRole: ConfigService.leadViewerUserRole
                });
            } finally {
                this.headerRendering = false;
            }
        }

        private async checkAndUpdateHeader(): Promise<void> {
            const shouldRefresh = sessionStorage.getItem('refreshHeaderAfterSignIn');

            if (shouldRefresh) {
                await new Promise(resolve => setTimeout(resolve, 500));
                await this.getUserData();

                if (this.userData && this.headerRenderer) {
                    const updateHeader = (window as any).updateHeader;
                    if (updateHeader) {
                        await updateHeader(this.headerRenderer, this.userData);
                    }
                }

                sessionStorage.removeItem('refreshHeaderAfterSignIn');
            }
        }

        changeLocale(locale: string): void {
            this.$i18n.locale = locale;
            localStorage.locale = locale;

            this.$nextTick(async () => {
                if (this.userData && this.headerRenderer && this.headerRenderer['config']) {
                    this.headerRenderer['config'].t = (key: string, params?: Record<string, any>) => {
                        return this.$t(key, params).toString();
                    };
                }

                await this.getUserData();

                if (this.userData && this.headerRenderer) {
                    const updateHeader = (window as any).updateHeader;
                    if (updateHeader) {
                        await updateHeader(this.headerRenderer, this.userData);
                    }
                }
            });

            ApiService.setLanguage(locale);
        }

        logout() {
            ApiService.logout().then(() => {
                this.setUserData(null);
                location.href = "/logout";
            });
        }

        onMenuItemClick(e: Event, menuLink: MenuLink) {
            if (menuLink.IsNotAllowed) {
                e.preventDefault();
                HelperService.showConfirmDialog(
                    i18n.t("NotAllowedPopup.Title").toString(),
                    i18n.t("NotAllowedPopup.Content").toString(),
                    i18n.t("NotAllowedPopup.Button").toString(),
                    i18n.t("Cancel").toString(),
                    () => {
                        window.open(menuLink.Href);
                    }
                );
            }
            if (!this.routeChanged) {
                if (menuLink.Href === leadsRoute && router.currentRoute.fullPath === leadsRoute
                    || menuLink.Href === clientLeadsRoute && router.currentRoute.fullPath === clientLeadsRoute
                    || menuLink.Href === clientsRoute && router.currentRoute.fullPath === clientsRoute) {
                    this.setNeedToRefreshLeadsGrid(RefreshGridMode.Full);
                } else if (menuLink.Href === campaignsRoute && router.currentRoute.fullPath === campaignsRoute) {
                    this.setNeedToRefreshCampaignsGrid(RefreshGridMode.Full);
                }
            }

            this.routeChanged = false;
        }

        showUserInfo(e: Event) {
            e.preventDefault();
            let messageParts: string[] = [];
            messageParts.push(`${i18n.t("Header.UserInfoPopup.Id")}: ${this.userData.Id}`);
            if (this.userData.CompanyId) {
                messageParts.push(`${i18n.t("Header.UserInfoPopup.CompanyId")}: ${this.userData.CompanyId}`);
            }
            messageParts.push(`${i18n.t("Header.UserInfoPopup.Name")}: ${this.userData.Name}`);
            messageParts.push(`${i18n.t("Header.UserInfoPopup.Email")}: ${this.userData.Email}`);
            messageParts.push(`${i18n.t("Header.AnyDesk")}`);
            HelperService.showMessage(
                i18n.t("Header.UserInfoPopup.Title").toString(),
                messageParts.join("<br />"),
                i18n.t("OK").toString()
            );
        }

        @Watch("userData", { immediate: false })
        async onUserDataChanged(newUserData: UserData | null, oldUserData: UserData | null): Promise<void> {
            if (newUserData && !this.headerRenderer) {
                await this.loadHeaderScript();
                await this.$nextTick();
                await this.renderUniversalHeader();
            }
            else if (newUserData && this.headerRenderer) {
                const updateHeader = (window as any).updateHeader;
                if (updateHeader) {
                    await updateHeader(this.headerRenderer, newUserData);
                }
            }
        }
    }
</script>
